import assign from 'object-assign';
import appLogger from '@core/LoggingProvider';
import Events from '@core/Events';
import CartService from '@services/CartService';
import find from 'ramda/src/find';
import curry from 'ramda/src/curry';
import reverse from 'ramda/src/reverse';
import path from 'ramda/src/path';
import namespace from '../../upi/namespace';

// todo -> add unit test for cartAddStrategy to verify items added in correct order

function _Cart () {
    const _instance = namespace( 'Cart' );

    if ( _instance.initialized ) {
        return _instance;
    }

    this.initialized = true;
    return this;
}

_Cart.prototype = assign( {}, Events, {

    /**
     * Fetch cart data from CartService api
     * @return {Object} - cart
     */
    fetch () {
        console.log( '[Cart.fetch] cartData' );
        return CartService.getCart();
    },

    /**
     *
     * @param skus
     * @param showConfirmation
     * @return {Promise.<Result>}
     */
    add_to_cart ( skus ) {
        const lineItems = sanitizeLineItems( skus );

        const resp = CartService
            .addLineItems( lineItems )
            .then( newCart => ({
                ...newCart,
                itemAdditions: lineItems
            }) )
            .catch( err => {
                console.error( `[Cart:add_to_cart]`, err );
                appLogger.capture( err );
                throw err;
            } );

        // note -> return the optimistic response
        return this.fetch();
    },

    /**
     *
     * @param skus
     * @return {Promise.<Result>}
     */
    update_items ( skus ) {
        const lineItems = sanitizeLineItems( skus );

        return Promise.resolve()
            .then( this._handleTransaction( lineItems, cartUpdatesStrategy ) )
    }
} );

const instance = new _Cart();

upi.Cart = instance;

export const Cart = instance;

/**
 * ------------------------------------------------
 * Cart - optimistic merge strategies
 */

/**
 * @param cartItemAdditions
 * @param mergeStrategy
 * @returns {Object} - updates cart state
 */
export const mergeCart = curry( ( mergeStrategy, state, cartItemAdditions ) => {
    if ( typeof mergeStrategy !== 'function' ) {
        throw new Error( `"mergeStrategy" param must be a function` )
    }

    const lineItems = mergeStrategy( state.lineItems, cartItemAdditions );

    return {
        ...state,
        lineItems,
        // warn -> [Estimated total] client cannot calculate subtotal accurately due to complex business logic
        subtotal: calculateSubtotal( lineItems )
    };
} );

/**
 * Calculate cart subtotal by accumulating item.linePrice * item.quantity
 * @param {Array} lineItems
 * @return {number}
 */
export function calculateSubtotal ( lineItems ) {
    if ( !Array.isArray( lineItems ) ) {
        return 0;
    }

    return lineItems.reduce( ( total, item ) => {
        const {quantity, linePrice, product: {quantityBreaks}} = item;
        const newPrice = getLineItemPrice( quantity, linePrice, quantityBreaks );

        if ( newPrice ) {
            total += newPrice * quantity;
        }

        return total;
    }, 0 );
}

/**
 *
 * @param qty
 * @param basePrice
 * @param {Array[{Quantity,Price}]} qtyBreaks = Array of {Quantity, Price} objects
 * @return {Number|null} - return calculated price break or null
 */
export function getLineItemPrice ( qty = 1, basePrice, qtyBreaks = [] ) {
    // if no basePrice (for restricted pricing when user is not logged in)
    if ( basePrice < 0 || isNaN( basePrice ) ) {
        return null;
    }

    // return basePrice if no quantity breaks available
    if ( !qtyBreaks || qtyBreaks.length <= 0 ) {
        return basePrice;
    }

    // -> lineItem adjusted price
    return Array.from( qtyBreaks )
        .map( i => ({...i, quantity: parseInt( i.quantity, 10 )}) )
        .sort( ( a, b ) => a.quantity - b.quantity )
        // set best available price provided qty if less than basePrice -> default basePrice
        .reduce( ( price, next ) => {
            price = (qty >= next.quantity && next.price < price) ? next.price : price;
            return price;
        }, basePrice );
}

/**
 * Apply a merge against 2 cart item collections, if SKu already exists in collection the Quantity will be incremented
 * by the specified amount
 * @param items - current cart items
 * @param addedItems - cart items to update
 * @returns {Array} - updated cart items
 */
export function cartAdditionsStrategy ( items, addedItems ) {
    if ( !Array.isArray( addedItems ) ) {
        addedItems = [addedItems];
    }
    if ( !Array.isArray( items ) ) {
        return addedItems;
    }
    const newLineItems = addedItems.reduce( ( acc, next ) => {
        // if sku already in cart -> increment "Quantity"
        let exists = find( findUserAddedSku( next ), acc );

        if ( exists ) {
            return acc.map( lineItem => {
                if ( lineItem.autoAddedItem || lineItem.product.sku !== next.product.sku ) {
                    return lineItem;
                }

                return {
                    ...lineItem,
                    quantity: lineItem.quantity + next.quantity
                }
            } );
        }

        // console.log( '[ADD]', next );
        // else append to cart items
        return acc.concat( [assign( {}, next )] );
    }, reverse( items ) );

    // we flip flop lineItems during reduce to put added items at top of list
    return reverse( newLineItems );
}

/**
 * Apply an update against 2 cart lineItem collections
 * @param lineItems - current cart lineItems
 * @param updatedItems - cart lineItems to update
 * @returns {Array} - updated cart lineItems
 */
export function cartUpdatesStrategy ( lineItems, updatedItems ) {
    if ( !Array.isArray( lineItems ) ) {
        return [];
    }

    if ( !Array.isArray( updatedItems ) ) {
        updatedItems = [updatedItems];
    }

    return lineItems
        .map( item => {
            let exists = find( findUserAddedSku( item ), updatedItems );
            if ( exists && !item.autoAddedItem ) {
                return {
                    ...item,
                    quantity: exists.quantity
                };
            }

            return item;
        } );
}

const findUserAddedSku = target => lineItem => {
    const lineSku = path( ['product', 'sku'], lineItem );
    const targetSku = path( ['product', 'sku'], target );

    if ( lineItem.autoAddedItem || !lineSku || !targetSku ) {
        return false;
    }

    return lineSku === targetSku;
};

/**
 * Apply an update against 2 cart lineItem collections
 * @param lineItems - current cart lineItems
 * @param removedItems - cart lineItems to remove
 * @returns {Array} - updated cart lineItems
 */
export function cartDeletionsStrategy ( lineItems, removedItems ) {
    if ( !Array.isArray( lineItems ) ) {
        return [];
    }

    if ( !Array.isArray( removedItems ) ) {
        removedItems = [removedItems];
    }

    const removals = removedItems.map( i => i.product.sku );
    return lineItems.filter( i => i.autoAddedItem || !removals.includes( i.product.sku ) );
}

/**
 * Coerce lineItems to array and ensure a "quantity" is set
 * @param lineItems
 */
function sanitizeLineItems ( lineItems ) {
    // todo: add SKU model validation to verify required fields are present
    if ( !Array.isArray( lineItems ) ) {
        lineItems = [lineItems];
    }

    // use "quantity" if provided or default to 1
    return lineItems.map( item => ({
        ...item,
        quantity: parseInt( item.quantity, 10 ) || 1
    }) );
}

/**
 * ------------------------------------------------
 * ApolloClient query transformations
 * client queries require a "__typename" to be set on each entity when working
 * with queries and the client cache
 */

export function toCartEntity( cartDetail = {} ) {
    return {
        __typename: 'Cart',
        id: 0,
        poNumber: '',
        subtotal: '0',
        total: '0',
        lineItems: [],
        ...cartDetail
    };
}

/**
 * Transform bare lineItem model to GraphQL schema format which ApolloClient requires
 * @param lineItem
 * @return {{__typename: string, product: {__typename: string, images, quantityBreaks}}}
 */
export function toLineItemEntity ( lineItem ) {
    let {product, product: {quantityBreaks, images}} = lineItem;
    let quantityBreakType = {__typename: 'QuantityBreak', price: product.catalogPrice, quantity: 999};
    let imageType = {__typename: 'FileAsset'};

    return {
        // sane defaults
        __typename: 'CartLineItem',
        id: Math.floor( Math.random() * -100000 ),
        linePrice: product.catalogPrice,
        quantity: 0,
        autoAddedItem: lineItem.autoAddedItem || false,
        ...lineItem,
        product: {
            __typename: 'Product',
            ...product,
            images: Array.isArray( images )
                ? images.map( i => ({...i, ...imageType}) )
                : [imageType],
            quantityBreaks: Array.isArray( quantityBreaks )
                ? quantityBreaks.map( i => ({...i, ...quantityBreakType}) )
                : [quantityBreakType]
        }
    }
}

export const toLineItemInput = lineItems => {
    if ( !Array.isArray( lineItems ) ) {
        lineItems = [lineItems]
    }

    return {
        input: {
            lineItems: lineItems.map( ( {product, quantity} ) => ({sku: product.sku, quantity}) )
        }
    }
};

export const toLineItemsPayload = ( mutationName, cart, changedLineItems ) => ({
    __typename: 'Mutation',
    [mutationName]: {
        __typename: 'LineItemsPayload',
        cart: {
            ...cart,
            total: null,
            __typename: 'Cart'
        },
        changedLineItems: changedLineItems.map( item => ({
            id: Math.random() * -100000,
            ...item
        }) )
    }
});
